import { useMemo } from "react";
import * as THREE from "three";
import { XR_PALETTE } from "./xrAssets";

const SUN = new THREE.Vector3(-0.55, 0.22, -0.8).normalize();

/** Gradient sky dome: deep navy zenith → soft teal horizon, with a warm golden glow toward the low sun. */
export const Sky = () => {
  const mat = useMemo(() => new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, fog: false,
    uniforms: { top: { value: new THREE.Color(XR_PALETTE.navy) }, mid: { value: new THREE.Color("#2E7F93") }, horizon: { value: new THREE.Color("#9ED9CF") }, sun: { value: new THREE.Color("#FFD58A") }, sunDir: { value: SUN } },
    vertexShader: "varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }",
    fragmentShader: `uniform vec3 top; uniform vec3 mid; uniform vec3 horizon; uniform vec3 sun; uniform vec3 sunDir; varying vec3 vDir;
      void main(){ float h = clamp(vDir.y, -0.1, 1.0);
        vec3 c = mix(horizon, mid, smoothstep(0.0, 0.28, h)); c = mix(c, top, smoothstep(0.25, 0.85, h));
        float s = max(dot(normalize(vDir), sunDir), 0.0);
        c += sun * (pow(s, 6.0) * 0.45 + pow(s, 220.0) * 1.6) * (1.0 - smoothstep(0.3, 0.9, h));
        gl_FragColor = vec4(c, 1.0);
        #include <colorspace_fragment>
      }`,
  }), []);
  return <mesh material={mat} scale={120}><sphereGeometry args={[1, 32, 16]} /></mesh>;
};

const groundTexture = () => {
  const c = document.createElement("canvas"); c.width = c.height = 512;
  const g = c.getContext("2d");
  g.fillStyle = "#C9A46A"; g.fillRect(0, 0, 512, 512);
  const blot = (n, colors, r0, r1, a) => { for (let i = 0; i < n; i++) { g.globalAlpha = a * Math.random(); g.fillStyle = colors[i % colors.length]; g.beginPath(); g.arc(Math.random() * 512, Math.random() * 512, r0 + Math.random() * r1, 0, Math.PI * 2); g.fill(); } };
  blot(260, ["#B98F55", "#D8B67A", "#A88148"], 6, 30, 0.28);
  blot(140, ["#8E9A4E", "#7C8C46", "#A3A55A"], 8, 34, 0.25);
  blot(1600, ["#E6CC95", "#9C7744"], 0.6, 1.6, 0.6);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(36, 36); t.anisotropy = 4;
  return t;
};

/** Stylised savanna floor (PBR, procedural texture) with a soft teal-tinted path ring around the patient. */
export const Ground = () => {
  const tex = useMemo(groundTexture, []);
  return (
    <group>
      <mesh rotation-x={-Math.PI / 2} receiveShadow><circleGeometry args={[70, 64]} /><meshStandardMaterial map={tex} roughness={0.95} metalness={0} /></mesh>
      <mesh rotation-x={-Math.PI / 2} position-y={0.005}><ringGeometry args={[1.1, 1.22, 64]} /><meshStandardMaterial color={XR_PALETTE.teal} emissive={XR_PALETTE.teal} emissiveIntensity={0.35} transparent opacity={0.55} roughness={0.6} /></mesh>
    </group>
  );
};

const rand = (s) => () => ((s = (s * 16807) % 2147483647) / 2147483647);

/** Instanced grass tufts + far acacia silhouettes + rounded village huts (cheap: 4 draw calls). */
export const Scenery = () => {
  const grass = useMemo(() => {
    const r = rand(7), m = new THREE.Matrix4(), list = [];
    for (let i = 0; i < 420; i++) {
      const a = r() * Math.PI * 2, d = 2.2 + r() * 26;
      m.compose(new THREE.Vector3(Math.cos(a) * d, 0, Math.sin(a) * d), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, r() * 6, (r() - 0.5) * 0.3)), new THREE.Vector3(1, 0.6 + r() * 0.9, 1).multiplyScalar(0.7 + r() * 0.6));
      list.push(m.clone());
    }
    return list;
  }, []);
  const acacias = useMemo(() => { const r = rand(3); return Array.from({ length: 14 }, () => { const a = -Math.PI * (0.05 + r() * 0.9) - Math.PI * 0.0, d = 24 + r() * 18; return [Math.cos(a) * d, Math.sin(a) * d, 0.8 + r() * 0.7]; }); }, []);
  const huts = [[-4.8, -6.5, 1.2], [5.2, -6, 1.05], [-7.6, -2.2, 0.95], [7.8, -1.5, 1.1]];
  return (
    <group>
      <instancedMesh args={[null, null, grass.length]} ref={(im) => { if (im) { grass.forEach((m, i) => im.setMatrixAt(i, m)); im.instanceMatrix.needsUpdate = true; } }}>
        <coneGeometry args={[0.07, 0.32, 4]} /><meshStandardMaterial color="#7E9446" roughness={0.9} />
      </instancedMesh>
      {acacias.map(([x, z, s], i) => (
        <group key={i} position={[x, 0, z]} scale={s}>
          <mesh position-y={1.4}><cylinderGeometry args={[0.08, 0.14, 2.8, 5]} /><meshStandardMaterial color="#4A3A2C" roughness={1} /></mesh>
          <mesh position-y={2.9} scale={[2.6, 0.45, 2.6]}><sphereGeometry args={[1, 10, 6]} /><meshStandardMaterial color="#2F5E4E" roughness={1} /></mesh>
        </group>
      ))}
      {huts.map(([x, z, s], i) => (
        <group key={i} position={[x, 0, z]} scale={s}>
          <mesh position-y={0.75} castShadow><cylinderGeometry args={[1.1, 1.2, 1.5, 18]} /><meshStandardMaterial color="#C98B5A" roughness={0.85} /></mesh>
          <mesh position-y={2.05} castShadow><coneGeometry args={[1.55, 1.2, 18]} /><meshStandardMaterial color="#9C7A3F" roughness={0.95} /></mesh>
          <mesh position={[0, 0.55, 1.13]}><planeGeometry args={[0.55, 0.9]} /><meshStandardMaterial color={XR_PALETTE.navy} roughness={0.8} /></mesh>
        </group>
      ))}
    </group>
  );
};

/** Warm low sun (one shadow map, rendered once = baked) + hemisphere sky/ground + soft ambient. */
export const Lights = () => (
  <>
    <ambientLight intensity={0.35} color="#FFF1DC" />
    <hemisphereLight args={["#BFEDE6", "#8A6A3F", 1.05]} />
    <directionalLight position={SUN.clone().multiplyScalar(30).toArray()} intensity={2.4} color="#FFD49A" castShadow
      shadow-mapSize={[1024, 1024]} shadow-camera-left={-12} shadow-camera-right={12} shadow-camera-top={12} shadow-camera-bottom={-12} shadow-camera-far={80} shadow-bias={-0.0004} shadow-radius={4} />
    <directionalLight position={[4, 6, 6]} intensity={0.5} color={XR_PALETTE.teal} />
  </>
);

/** Blob contact shadow (no dynamic cost) under animated characters. */
export const Blob = ({ r = 0.45, ...p }) => {
  const tex = useMemo(() => {
    const c = document.createElement("canvas"); c.width = c.height = 128;
    const g = c.getContext("2d"), gr = g.createRadialGradient(64, 64, 4, 64, 64, 64);
    gr.addColorStop(0, "rgba(10,37,64,0.55)"); gr.addColorStop(1, "rgba(10,37,64,0)");
    g.fillStyle = gr; g.fillRect(0, 0, 128, 128);
    return new THREE.CanvasTexture(c);
  }, []);
  return <mesh rotation-x={-Math.PI / 2} position-y={0.01} {...p}><planeGeometry args={[r * 2, r * 2]} /><meshBasicMaterial map={tex} transparent depthWrite={false} /></mesh>;
};
